// Middlebury College: public college page (#2739), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Middlebury's Common Data Set for Fall 2025 (the file Middlebury posts as
//    "Fall 2025 CDS FINAL"): C1 applicants, admits and enrollees for Fall
//    2025, C8 test policy for Fall 2027, C21/C22 plans with early decision
//    counts for the Fall 2025 entering class. Quirks: the PDF's running
//    header still reads "Common Data Set 2024-2025" while its C1 reports
//    Fall 2025 and its C8 names Fall 2027, so it is labelled here as the
//    2025-2026 edition; its admit counts are September admits only (they
//    leave out students admitted for February entry), and its early
//    decision admit count also leaves out February admits. Its G1 gives
//    2025-2026 charges, so costs come from Student Financial Services'
//    2026-2027 figures. The C1 residency table gives totals only.
//  - middlebury.edu admissions: Application Instructions and Deadlines
//    (calendar for this cycle, test-optional policy, entry term options).
//  - College Scorecard API, school id 230959 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Middlebury Common Data Set 2025-2026 (Fall 2025)',
        url: 'https://www.middlebury.edu/sites/default/files/2026-06/Fall%202025%20CDS%20FINAL.pdf',
        read: '2026-10-01',
    },
    apply: {
        label: 'Middlebury Admissions, Application Instructions and Deadlines',
        url: 'https://www.middlebury.edu/college/admissions/application-instructions-and-deadlines',
        read: '2026-10-01',
    },
    costs: {
        label: 'Middlebury Student Financial Services, Tuition and Fees 2026-2027',
        url: 'https://www.middlebury.edu/student-financial-services/tuition-fees-and-payment/tuition-and-fees',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?230959-Middlebury-College',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'middlebury-college',
    kbId: 'middlebury_college',
    name: 'Middlebury College',
    shortName: 'Middlebury',
    aliases: ['Midd'],
    location: 'Middlebury, VT',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (middlebury.edu); decoration only.
    branding: {
        primary: '#0D395F',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/middlebury_college_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 11831,
        admitted: 1511,
        enrolled: 634,
        source: 'cds',
        where: 'section C1 (admits are September admits only; students admitted for February entry are not counted)',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Middlebury\'s Common Data Set reports one first-year acceptance rate for the whole college, with no rate by major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision',
        note: 'Counts for both early decision rounds together. The admit count leaves out students admitted for February entry. Middlebury has no early action plan.',
        year: 'Fall 2025',
        rows: [
            { plan: 'Early Decision I and II', applied: 1179, admitted: 460 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 4' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 2', decision: 'Mid-December', binding: true },
            { plan: 'Early Decision II', deadline: 'January 4', decision: 'Mid-February', binding: true },
            { plan: 'Regular Decision', deadline: 'January 4', decision: 'Late March', binding: false },
        ],
        note: 'Middlebury takes the Common App, the Coalition Application and QuestBridge, with no supplemental essay. You can ask to start in September, in February (a "Feb") or either; the deadlines are the same. The deadline to request an optional alumni interview is November 8 for Early Decision I, January 9 for Early Decision II and January 16 for Regular Decision. No decision is made without senior grades.',
        source: 'apply',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Middlebury is test-optional. If you think your score reflects your potential you can send it, and it is considered in your review; you can report your scores yourself when you apply. The ACT science section is not required.',
        source: 'apply',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as considered if submitted, not required, for fall 2027 applicants.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'apply',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 72924 },
            { item: 'Student activity fee', amount: 542 },
            { item: 'Housing and meal plan (on campus)', amount: 20920 },
            { item: 'Books, course materials and supplies', amount: 1000 },
            { item: 'Transportation', amount: 1000 },
            { item: 'Personal expenses', amount: 1000 },
        ],
        total: 97386,
        note: 'For a full-time undergraduate living on campus in fall 2026 and spring 2027. Transportation varies by location. Students must also have health insurance.',
        source: 'costs',
        netPrice: {
            average: 32102,
            byIncome: [
                { income: '$0 to $30,000', amount: 7548 },
                { income: '$30,001 to $48,000', amount: 8388 },
                { income: '$48,001 to $75,000', amount: 13993 },
                { income: '$75,001 to $110,000', amount: 23522 },
                { income: 'Over $110,000', amount: 50664 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Middlebury\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
